import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect } from 'react';

import CalendarField from '../../components/CalendarField/CalendarField';
import HabitationFormField from '../../components/HabitationFormField/HabitationFormField';
import MonetaryField from '../../components/MonetaryField/MonetaryField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';
import { AttributeNames } from '../../types/AttributeNames';

const AccommodationPage = () => {
    useComponentTracker('AccommodationPage');
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();

    const habitationForm = useAppSelector(selectFieldValue)('applicantHabitationForm');
    // DK's "Lejebolig" (rental) option is submitted as APARTMENT, matching the NO flow's equivalent.
    const showAccommodationCost = habitationForm === 'APARTMENT';

    const validationErrors = useValidation(
        showAccommodationCost
            ? ['applicantHabitationForm', 'applicantHabitationYear', AttributeNames.applicantAccomodationCost]
            : ['applicantHabitationForm', 'applicantHabitationYear'],
    );

    useSetBannerType('bannerOne');

    // Clear the accommodation-cost amount (and any stale error) when it is no longer shown, so it is
    // neither submitted nor re-displayed as invalid if the field is shown again.
    useEffect(() => {
        if (!showAccommodationCost) {
            dispatch(
                addOrUpdateFormField({
                    fieldName: AttributeNames.applicantAccomodationCost,
                    value: undefined,
                    hasError: false,
                    errorMessage: undefined,
                }),
            );
        }
    }, [showAccommodationCost, dispatch]);

    const minValueValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = Number(value ?? 0) > 0;

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : 'field.applicant.min-value.error',
        };
    };

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });

            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.accommodation.label')} />
            <HabitationFormField name="applicantHabitationForm" />
            <Box sx={{ mt: 2 }}>
                <CalendarField
                    name="applicantHabitationYear"
                    label={translate('field.applicant-habitation-year.label')}
                    minDate={dayjs('1900-01-01')}
                    maxDate={dayjs().endOf('year')}
                />
            </Box>
            {showAccommodationCost && (
                <Box sx={{ mt: 2 }}>
                    <MonetaryField
                        name={AttributeNames.applicantAccomodationCost}
                        label={translate('field.applicant-rent-share.label')}
                        customValidation={minValueValidation}
                    />
                </Box>
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default AccommodationPage;
